import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { 
  Users, 
  Plus, 
  Trash2,
  Archive,
  ArchiveRestore,
  ChevronDown,
  BarChart3,
  Play,
  Pause,
  Square,
  Download
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import io from 'socket.io-client';
import { getSocketUrl } from '@/utils/socketUrl';
import AiClassmateFields from '@/components/AiClassmateFields';
import { DEFAULT_AI_STUDENT_NAME, DEFAULT_AI_STUDENT_DESCRIPTION } from '@/config/aiStudent';

interface Room {
  id: number;
  name: string;
  subject_id: number;
  created_by: number;
  min_success_score: number;
  hint_threshold: number;
  allowed_consequent_answers: number;
  current_stage_id: number | null;
  current_question_id: number | null;
  status: 'active' | 'paused' | 'completed' | 'archived';
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  is_personal?: boolean | number;
  personal_student_id?: number | null;
  personal_student_name?: string | null;
  personal_student_university?: string | null;
  subject?: {
    name: string;
  };
  creator?: {
    username: string;
    name: string;
  };
  member_count?: number;
}

interface Subject {
  id: number;
  name: string;
  summary: string;
}

interface RoomFolder {
  key: string;
  label: string | null;
  sortKey: string;
  rooms: Room[];
}

interface StudentStat {
  student_id: number;
  name?: string;
  username?: string;
  email?: string;
  mbti?: string;
  university?: string;
  questions_answered: number;
  average_score: number;
}

const RoomBrowserPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [rooms, setRooms] = useState<Room[]>([]);
  const roomsRef = useRef<Room[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<Room | null>(null);
  const [createRoomTab, setCreateRoomTab] = useState<'single' | 'all'>('single');
  const [createForm, setCreateForm] = useState({
    name: '',
    subjectId: '',
    minSuccessScore: 70,
    hintThreshold: 2,
    allowedConsequentAnswers: 1,
    modifier: '',
    aiStudentName: DEFAULT_AI_STUDENT_NAME,
    aiStudentDescription: DEFAULT_AI_STUDENT_DESCRIPTION
  });
  const [collapsedFolders, setCollapsedFolders] = useState<Record<string, boolean>>({});
  const [expandedRoomId, setExpandedRoomId] = useState<number | null>(null);
  const [roomStats, setRoomStats] = useState<Record<number, StudentStat[]>>({});
  const [statsLoading, setStatsLoading] = useState<Record<number, boolean>>({});
  const [statsError, setStatsError] = useState<Record<number, string | null>>({});
  const [folderActionLoading, setFolderActionLoading] = useState<Record<string, string | null>>({});
  const [exportingFolder, setExportingFolder] = useState<string | null>(null);

  const hasFetched = useRef(false);
  const isFetching = useRef(false);
  
  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);

  useEffect(() => {
    if (isAuthenticated && user && !hasFetched.current) {
      hasFetched.current = true;
      fetchRooms();
    }
  }, [isAuthenticated, user]);

  const canShowRoom = useCallback((room: Room) => {
    if (user?.role !== 'student') {
      return true;
    }

    const isPersonalRoom = room.is_personal === true || room.is_personal === 1;
    return !isPersonalRoom || room.personal_student_id === user.id;
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const socket = io(getSocketUrl(), {
      auth: {
        token: localStorage.getItem('teaching_app_token')
      },
      transports: ['websocket', 'polling']
    });

    socket.on('connect_error', (error) => {
      console.error('RoomBrowserPage socket connection error:', error);
    });

    const handleRoomCreated = (data: { room: Room }) => {
      setRooms(prev => {
        if (prev.some(room => room.id === data.room.id) || !canShowRoom(data.room)) {
          return prev;
        }

        return [data.room, ...prev];
      });
    };

    const handleRoomUpdated = (data: { room: Room }) => {
      setRooms(prev => {
        const exists = prev.some(room => room.id === data.room.id);
        if (exists) {
          return prev.map(room =>
            room.id === data.room.id ? { ...room, ...data.room } : room
          );
        }

        return canShowRoom(data.room) ? [data.room, ...prev] : prev;
      });
    };

    const handleRoomDeleted = (data: { roomId: number }) => {
      setRooms(prev => prev.filter(room => room.id !== data.roomId));
    };

    const handleAnswerEvaluated = (data: { roomId: number; studentId: number; score: number; isCorrect: boolean }) => {
      if (expandedRoomId === data.roomId) {
        fetchRoomStats(data.roomId);
      }
    };

    socket.on('room_created', handleRoomCreated);
    socket.on('room_updated', handleRoomUpdated);
    socket.on('room_deleted', handleRoomDeleted);
    socket.on('answer_evaluated', handleAnswerEvaluated);

    return () => {
      socket.disconnect();
    };
  }, [canShowRoom, expandedRoomId, isAuthenticated]);


  const fetchRooms = async () => {
    if (isFetching.current) {
      return;
    }

    try {
      isFetching.current = true;
      setLoading(true);
      
      const response = await fetch('/api/rooms', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRooms(Array.isArray(data.rooms) ? data.rooms : []);
      } else if (response.status === 429) {
        console.warn('Rate limited (429), please wait a moment and refresh');
        setRooms([]);
      } else {
        console.error('Rooms API Error:', response.status, response.statusText);
        setRooms([]);
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
      setRooms([]);
    } finally {
      isFetching.current = false;
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await fetch('/api/subjects', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubjects(data.subjects || []);
      } else {
        console.error('Subjects API Error:', response.status, response.statusText);
        setSubjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      setSubjects([]);
    }
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let endpoint = '/api/rooms';
      let requestBody: any = createForm;
      
      // If creating for all students, use different endpoint; omit name and consecutive-answers (server uses default for personal rooms)
      if (createRoomTab === 'all') {
        endpoint = '/api/rooms/create-for-all-students';
        const { name, allowedConsequentAnswers: _ac, aiStudentName: _an, aiStudentDescription: _ad, ...bodyForBulk } = createForm;
        requestBody = bodyForBulk;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        
        // Show success message
        if (createRoomTab === 'all') {
          alert(`Successfully created ${data.count} personal room${data.count > 1 ? 's' : ''} for all students!`);
        } else {
          alert('Room created successfully!');
        }
        
        // Don't add room here - let Socket.IO handle it for real-time updates
        setShowCreateModal(false);
        setCreateRoomTab('single'); // Reset to default tab
        setCreateForm({
          name: '',
          subjectId: '',
          minSuccessScore: 70,
          hintThreshold: 2,
          allowedConsequentAnswers: 1,
          modifier: '',
          aiStudentName: DEFAULT_AI_STUDENT_NAME,
          aiStudentDescription: DEFAULT_AI_STUDENT_DESCRIPTION
        });
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create room');
      }
    } catch (error) {
      console.error('Failed to create room:', error);
      alert('Failed to create room');
    }
  };

  const handleJoinRoom = async (roomId: number) => {
    try {
      const response = await fetch(`/api/rooms/${roomId}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        // Tell ChatPage not to POST auto-start-personal: join already kicked off background startSession
        navigate(`/chat/${roomId}`, {
          state: { skipDuplicatePersonalAutoStart: true }
        });
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to join room');
      }
    } catch (error) {
      console.error('Failed to join room:', error);
      alert('Failed to join room');
    }
  };

  const handleArchiveRoom = async (roomId: number) => {
    try {
      const response = await fetch(`/api/rooms/${roomId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        // Refresh rooms list
        fetchRooms();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to archive room');
      }
    } catch (error) {
      console.error('Failed to archive room:', error);
      alert('Failed to archive room');
    }
  };

  const handleUnarchiveRoom = async (roomId: number) => {
    try {
      const response = await fetch(`/api/rooms/${roomId}/unarchive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        // Refresh rooms list
        fetchRooms();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to unarchive room');
      }
    } catch (error) {
      console.error('Failed to unarchive room:', error);
      alert('Failed to unarchive room');
    }
  };

  const handleDeleteRoom = async () => {
    if (!roomToDelete) return;

    try {
      const response = await fetch(`/api/rooms/${roomToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        // Remove room from list
        setRooms(prev => prev.filter(room => room.id !== roomToDelete.id));
        setShowDeleteModal(false);
        setRoomToDelete(null);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete room');
      }
    } catch (error) {
      console.error('Failed to delete room:', error);
      alert('Failed to delete room');
    }
  };

  const openDeleteModal = (room: Room) => {
    setRoomToDelete(room);
    setShowDeleteModal(true);
  };

  const fetchRoomStats = async (roomId: number) => {
    setStatsLoading(prev => ({ ...prev, [roomId]: true }));
    setStatsError(prev => ({ ...prev, [roomId]: null }));

    try {
      const response = await fetch(`/api/rooms/${roomId}/student-stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRoomStats(prev => ({
          ...prev,
          [roomId]: Array.isArray(data.students) ? data.students : []
        }));
      } else {
        const error = await response.json().catch(() => ({}));
        setStatsError(prev => ({
          ...prev,
          [roomId]: error?.error || 'Failed to load student performance'
        }));
      }
    } catch (error) {
      console.error('Failed to fetch room stats:', error);
      setStatsError(prev => ({
        ...prev,
        [roomId]: 'Failed to load student performance'
      }));
    } finally {
      setStatsLoading(prev => ({ ...prev, [roomId]: false }));
    }
  };

  const toggleRoomStats = (roomId: number) => {
    const isCurrentlyExpanded = expandedRoomId === roomId;
    const nextId = isCurrentlyExpanded ? null : roomId;
    
    setExpandedRoomId(nextId);
    
    // Always fetch latest data when expanding
    if (!isCurrentlyExpanded && nextId !== null) {
      fetchRoomStats(roomId);
    }
  };

  const roomsArray = useMemo(() => Array.isArray(rooms) ? rooms : [], [rooms]);
  
  // Filter rooms by active/archived status and search term (like SubjectEditorPage)
  const activeRooms = roomsArray.filter(room => {
    if (!room?.name || !room.status) {
      return false;
    }
    
    const roomName = room.name.toLowerCase();
    const subjectName = room.subject?.name?.toLowerCase() || '';
    const searchLower = (searchTerm || '').toLowerCase();
    
    const isNotArchived = room.status !== 'archived';
    const matchesSearch = roomName.includes(searchLower) || subjectName.includes(searchLower);
    
    return isNotArchived && matchesSearch;
  });
  
  const archivedRooms = roomsArray.filter(room => {
    if (!room?.name || !room.status) {
      return false;
    }
    
    const roomName = room.name.toLowerCase();
    const subjectName = room.subject?.name?.toLowerCase() || '';
    const searchLower = (searchTerm || '').toLowerCase();
    
    const isArchived = room.status === 'archived';
    const matchesSearch = roomName.includes(searchLower) || subjectName.includes(searchLower);
    
    return isArchived && matchesSearch;
  });
  
  // For students, only show active rooms. For curators, show based on activeTab
  const filteredRooms = user?.role === 'curator' 
    ? (activeTab === 'active' ? activeRooms : archivedRooms)
    : activeRooms;

  const groupedRooms = useMemo<RoomFolder[]>(() => {
    const isCurator = user?.role === 'curator';

    if (!isCurator) {
      return [{
        key: 'student-view',
        label: null,
        sortKey: 'student-view',
        rooms: filteredRooms
      }];
    }

    const foldersMap = new Map<string, RoomFolder>();

    filteredRooms.forEach((room) => {
      if (!room) return;

      const subjectName = room.subject?.name || 'Unknown Subject';
      const createdDate = room.created_at ? new Date(room.created_at) : undefined;

      let dateKey = 'unknown-date';
      let dateLabel = 'Unknown Date';

      if (createdDate instanceof Date && !isNaN(createdDate.getTime())) {
        const isoDate = createdDate.toISOString();
        const [isoDatePart] = isoDate.split('T');
        dateKey = isoDatePart || isoDate;
        dateLabel = createdDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }

      const folderKey = `${dateKey}_${subjectName}`;
      if (!foldersMap.has(folderKey)) {
        foldersMap.set(folderKey, {
          key: folderKey,
          label: `${dateLabel} • ${subjectName}`,
          sortKey: dateKey,
          rooms: [] as Room[]
        });
      }

      const targetFolder = foldersMap.get(folderKey);
      if (targetFolder) {
        targetFolder.rooms.push(room);
      }
    });

    // Preserve chronological order (newest first)
    return Array.from(foldersMap.values()).sort((a, b) => {
      if (a.sortKey === b.sortKey) return 0;
      return a.sortKey < b.sortKey ? 1 : -1;
    });
  }, [filteredRooms, user?.role]);

  useEffect(() => {
    setCollapsedFolders(prev => {
      let hasChanges = false;
      const nextState = { ...prev };
      groupedRooms.forEach(folder => {
        if (!(folder.key in nextState)) {
          nextState[folder.key] = false;
          hasChanges = true;
        }
      });
      return hasChanges ? nextState : prev;
    });
  }, [groupedRooms]);

  const toggleFolder = (folderKey: string) => {
    setCollapsedFolders(prev => ({
      ...prev,
      [folderKey]: !prev[folderKey]
    }));
  };

  // Helper to determine folder status
  const getFolderStatus = (folder: RoomFolder): 'active' | 'paused' | 'inactive' => {
    // Check room statuses in the folder
    // A room is considered:
    // - 'active': status is 'active' AND started_at is not null (session is running)
    // - 'paused': status is 'paused' (session was started and paused)
    // - 'inactive': no session started or session completed
    
    const hasActiveRoom = folder.rooms.some(room => 
      room.status === 'active' && room.started_at !== null
    );
    
    const hasPausedRoom = folder.rooms.some(room => 
      room.status === 'paused'
    );
    
    // If any room is actively running, show pause/stop buttons
    if (hasActiveRoom) {
      return 'active';
    }
    
    // If any room is paused (but none are active), show start button (to resume)
    if (hasPausedRoom) {
      return 'paused';
    }
    
    // Otherwise, show start button
    return 'inactive';
  };

  const handleFolderStart = async (folder: RoomFolder, e: React.MouseEvent) => {
    e.stopPropagation();
    const folderKey = folder.key;
    setFolderActionLoading(prev => ({ ...prev, [folderKey]: 'start' }));

    try {
      const roomsToStart = folder.rooms.filter(room => 
        user?.role === 'curator' && 
        room.created_by === user.id && 
        (room.started_at === null || room.status === 'completed' || room.status === 'paused')
      );

      if (roomsToStart.length === 0) {
        setFolderActionLoading(prev => ({ ...prev, [folderKey]: null }));
        return;
      }

      const promises = roomsToStart.map(async (room) => {
        try {
          const endpoint = room.status === 'paused' 
            ? `/api/sessions/${room.id}/resume`
            : `/api/sessions/${room.id}/start`;
          
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
            }
          });
          
          if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.error(`Failed to ${room.status === 'paused' ? 'resume' : 'start'} room ${room.id}:`, error);
            throw new Error(error.error || `Failed to ${room.status === 'paused' ? 'resume' : 'start'} room ${room.id}`);
          }
          
          return { roomId: room.id, success: true };
        } catch (error) {
          console.error(`Error ${room.status === 'paused' ? 'resuming' : 'starting'} room ${room.id}:`, error);
          return { roomId: room.id, success: false, error };
        }
      });

      const results = await Promise.allSettled(promises);
      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failed = results.length - successful;
      
      if (failed > 0 && successful === 0) {
        alert('Failed to start sessions');
        setFolderActionLoading(prev => ({ ...prev, [folderKey]: null }));
        return;
      } else if (failed > 0) {
        alert(`Started ${successful} sessions, but ${failed} failed`);
      }
      
      const roomIdsToCheck = roomsToStart.map(r => r.id);
      let attempts = 0;
      const maxAttempts = 20;
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const currentRooms = roomsRef.current;
        const allUpdated = roomIdsToCheck.every(roomId => {
          const room = currentRooms.find(r => r.id === roomId);
          if (!room) return false;
          
          const originalRoom = roomsToStart.find(r => r.id === roomId);
          const hasStartedAt = room.started_at !== null && room.started_at !== undefined;
          const wasStarted = originalRoom && !originalRoom.started_at && hasStartedAt;
          const isActiveWithStartedAt = room.status === 'active' && hasStartedAt;
          const isPausedWithStartedAt = room.status === 'paused' && hasStartedAt;
          
          return isActiveWithStartedAt || isPausedWithStartedAt || wasStarted;
        });
        
        if (allUpdated) {
          break;
        }
        
        attempts++;
      }
    } catch (error) {
      console.error('Failed to start folder sessions:', error);
      alert('Failed to start some sessions');
    } finally {
      setFolderActionLoading(prev => ({ ...prev, [folderKey]: null }));
    }
  };

  const handleFolderPause = async (folder: RoomFolder, e: React.MouseEvent) => {
    e.stopPropagation();
    const folderKey = folder.key;
    setFolderActionLoading(prev => ({ ...prev, [folderKey]: 'pause' }));

    try {
      const roomsToPause = folder.rooms.filter(room => 
        user?.role === 'curator' && 
        room.created_by === user.id && 
        room.status === 'active' && 
        room.started_at !== null
      );

      if (roomsToPause.length === 0) {
        setFolderActionLoading(prev => ({ ...prev, [folderKey]: null }));
        return;
      }

      const promises = roomsToPause.map(async (room) => {
        try {
          const response = await fetch(`/api/sessions/${room.id}/pause`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
            }
          });
          
          if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.error(`Failed to pause room ${room.id}:`, error);
            throw new Error(error.error || `Failed to pause room ${room.id}`);
          }
          
          return { roomId: room.id, success: true };
        } catch (error) {
          console.error(`Error pausing room ${room.id}:`, error);
          return { roomId: room.id, success: false, error };
        }
      });

      await Promise.allSettled(promises);

      const roomIdsToCheck = roomsToPause.map(r => r.id);
      let attempts = 0;
      const maxAttempts = 20;
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const currentRooms = roomsRef.current;
        const allUpdated = roomIdsToCheck.every(roomId => {
          const room = currentRooms.find(r => r.id === roomId);
          if (!room) return false;
          return room.status === 'paused';
        });
        
        if (allUpdated) {
          break;
        }
        
        attempts++;
      }
    } catch (error) {
      console.error('Failed to pause folder sessions:', error);
      alert('Failed to pause some sessions');
    } finally {
      setFolderActionLoading(prev => ({ ...prev, [folderKey]: null }));
    }
  };

  const handleFolderStop = async (folder: RoomFolder, e: React.MouseEvent) => {
    e.stopPropagation();
    const folderKey = folder.key;
    setFolderActionLoading(prev => ({ ...prev, [folderKey]: 'stop' }));

    try {
      const roomsToStop = folder.rooms.filter(room => 
        user?.role === 'curator' && 
        room.created_by === user.id && 
        ((room.status === 'active' && room.started_at !== null) || room.status === 'paused')
      );

      if (roomsToStop.length === 0) {
        setFolderActionLoading(prev => ({ ...prev, [folderKey]: null }));
        return;
      }

      const promises = roomsToStop.map(async (room) => {
        try {
          const response = await fetch(`/api/sessions/${room.id}/complete`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
            }
          });
          
          if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.error(`Failed to stop room ${room.id}:`, error);
            throw new Error(error.error || `Failed to stop room ${room.id}`);
          }
          
          return { roomId: room.id, success: true };
        } catch (error) {
          console.error(`Error stopping room ${room.id}:`, error);
          return { roomId: room.id, success: false, error };
        }
      });

      await Promise.allSettled(promises);

      const roomIdsToCheck = roomsToStop.map(r => r.id);
      let attempts = 0;
      const maxAttempts = 20;
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const currentRooms = roomsRef.current;
        const allUpdated = roomIdsToCheck.every(roomId => {
          const room = currentRooms.find(r => r.id === roomId);
          if (!room) return false;
          return room.status === 'completed';
        });
        
        if (allUpdated) {
          break;
        }
        
        attempts++;
      }
    } catch (error) {
      console.error('Failed to stop folder sessions:', error);
      alert('Failed to stop some sessions');
    } finally {
      setFolderActionLoading(prev => ({ ...prev, [folderKey]: null }));
    }
  };

  const handleExportFolderStats = async (folder: RoomFolder, e: React.MouseEvent) => {
    e.stopPropagation();
    const folderKey = folder.key;
    
    if (folder.rooms.length === 0) {
      alert('No rooms in this folder to export');
      return;
    }

    setExportingFolder(folderKey);

    try {
      const roomIds = folder.rooms.map(room => room.id);
      const response = await fetch('/api/rooms/export-stats', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          roomIds,
          folderLabel: folder.label || null
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to export CSV' }));
        throw new Error(error.error || 'Failed to export CSV');
      }

      // Get the CSV content as blob
      const blob = await response.blob();
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'folder_stats.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export folder stats:', error);
      alert(error instanceof Error ? error.message : 'Failed to export CSV');
    } finally {
      setExportingFolder(null);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-[100svh] w-full bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-100/30 dark:from-gray-900 dark:via-slate-800 dark:to-slate-900">
      <PageHeader
        subtitle={user?.role === 'curator' ? 'Create and manage learning sessions' : 'Join learning sessions'}
        icon={Users}
        iconColor="text-blue-600 dark:text-blue-400"
        iconBgColor="bg-blue-100 dark:bg-blue-900/20"
        showBackButton={false}
        rightContent={
          user?.role === 'curator' && (
            <button
              onClick={() => {
                setShowCreateModal(true);
                fetchSubjects(); // Fetch subjects when opening the modal
              }}
              className="inline-flex items-center justify-center p-2 border border-transparent rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              title="Create Room"
            >
              <Plus className="h-5 w-5" />
            </button>
          )
        }
        // Search functionality
        showSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchPlaceholder="Search rooms..."
        // Tab functionality
        showTabs={user?.role === 'curator'}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <div 
        className="w-full py-3 sm:pb-3 overflow-y-auto h-[100svh]"
        style={{
          paddingTop: 'var(--header-height, 80px)'
        }}
      >
        {/* Rooms Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading rooms...</p>
          </div>
        ) : filteredRooms.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {searchTerm ? 'No rooms found' : 'No rooms available'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchTerm ? 'Try adjusting your search terms' : 'Be the first to create a learning room!'}
            </p>
            {user?.role === 'curator' && !searchTerm && (
              <button
                onClick={() => {
                  setShowCreateModal(true);
                  fetchSubjects(); // Fetch subjects when opening the modal
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Room
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden w-full"
            style={{width: '100%', maxWidth: '100%'}}
          >
            {groupedRooms.map((folder) => {
              const isCollapsed = collapsedFolders[folder.key] ?? false;
              const showRooms = !folder.label || !isCollapsed;
              return (
              <div key={folder.key}>
                {folder.label && (
                  <div className="w-full bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between px-4 py-2">
                      <button
                        type="button"
                        onClick={() => toggleFolder(folder.key)}
                        className="flex-1 flex items-center justify-between text-left hover:bg-gray-100 dark:hover:bg-gray-900/60 transition-colors -mx-4 px-4 py-2"
                      >
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{folder.label}</p>
                        <ChevronDown
                          className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${
                            isCollapsed ? '-rotate-90' : ''
                          }`}
                        />
                      </button>
                      {user?.role === 'curator' && (
                        <div className="flex items-center space-x-1 ml-2" onClick={(e) => e.stopPropagation()}>
                          {/* Export CSV button - always visible for curators */}
                          <button
                            onClick={(e) => handleExportFolderStats(folder, e)}
                            disabled={exportingFolder === folder.key || folder.rooms.length === 0}
                            className="p-1.5 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Export student statistics as CSV"
                          >
                            {exportingFolder === folder.key ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </button>
                          {/* Session control buttons - only for active tab */}
                          {activeTab === 'active' && (
                            <>
                              {getFolderStatus(folder) === 'inactive' || getFolderStatus(folder) === 'paused' ? (
                                <button
                                  onClick={(e) => handleFolderStart(folder, e)}
                                  disabled={folderActionLoading[folder.key] === 'start'}
                                  className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={getFolderStatus(folder) === 'paused' ? "Resume all sessions in folder" : "Start all sessions in folder"}
                                >
                                  {folderActionLoading[folder.key] === 'start' ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={(e) => handleFolderPause(folder, e)}
                                    disabled={folderActionLoading[folder.key] === 'pause'}
                                    className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Pause all sessions in folder"
                                  >
                                    {folderActionLoading[folder.key] === 'pause' ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                                    ) : (
                                      <Pause className="h-4 w-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={(e) => handleFolderStop(folder, e)}
                                    disabled={folderActionLoading[folder.key] === 'stop'}
                                    className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Stop all sessions in folder"
                                  >
                                    {folderActionLoading[folder.key] === 'stop' ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    ) : (
                                      <Square className="h-4 w-4" />
                                    )}
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {showRooms && folder.rooms.map((room, index) => (
                  <React.Fragment key={room.id}>
                    <div
                      className={`flex px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer border-b border-gray-100 dark:border-gray-700 w-full min-w-0 ${
                        index === folder.rooms.length - 1 ? 'last:border-b-0' : ''
                      }`}
                      style={{width: '100%', maxWidth: '100%'}}
                      onClick={() => user?.role === 'curator' && room.created_by === user.id ? navigate(`/chat/${room.id}`) : handleJoinRoom(room.id)}
                    >

                      {/* Room Content */}
                    <div className="flex-1 min-w-0 pr-4 overflow-hidden"
                      style={{minWidth: 0, maxWidth: 'calc(100% - 120px)'}}
                    >
                      <div className="flex items-center mb-1">
                        {/* Status Circle */}
                        <div className={`w-3 h-3 rounded-full mr-2 flex-shrink-0 ${
                          room.status === 'active' ? 'bg-green-500' :
                          room.status === 'paused' ? 'bg-yellow-500' :
                          room.status === 'completed' ? 'bg-blue-500' :
                          'bg-gray-400'
                        }`} />
                        <h3 className="text-base font-medium text-gray-900 dark:text-white truncate">
                          {room.is_personal
                            ? [
                                room.personal_student_university,
                                room.personal_student_name || room.name
                              ]
                                .filter(Boolean)
                                .join(' - ')
                            : room.name}
                        </h3>
                      </div>
                      
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-500 mb-1">
                        <span className="truncate">Created by: {room.creator?.name || 'Unknown'}</span>
                      </div>
                      
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-500 space-x-4">
                        {room.member_count && (
                          <span>Members: {room.member_count}</span>
                        )}
                      </div>
                    </div>

                      {/* Right Side Info and Actions */}
                    <div className="flex-shrink-0 flex flex-col items-end space-y-2 ml-auto">
                      {/* Date */}
                      <div className="flex flex-col items-end space-y-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(room.created_at).toLocaleDateString()}
                          </span>
                          <span className="text-[11px] text-gray-400 dark:text-gray-500">
                            {new Date(room.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-1">
                        {user?.role === 'curator' && (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRoomStats(room.id);
                              }}
                              className={`p-2 rounded-lg transition-colors ${expandedRoomId === room.id ? 'text-blue-700 bg-blue-50 dark:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
                              title="View student performance"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </button>
                            {room.created_by === user.id && (
                              <>
                                {room.status !== 'archived' ? (
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleArchiveRoom(room.id);
                                    }}
                                    className="p-2 text-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
                                    title="Archive Room"
                                  >
                                    <Archive className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <>
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnarchiveRoom(room.id);
                                      }}
                                      className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                      title="Unarchive Room"
                                    >
                                      <ArchiveRestore className="h-4 w-4" />
                                    </button>
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDeleteModal(room);
                                      }}
                                      className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                      title="Delete Room"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {expandedRoomId === room.id && (
                    <div className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-200 dark:border-gray-700">
                      {statsLoading[room.id] ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading student performance...</p>
                      ) : statsError[room.id] ? (
                        <p className="text-sm text-red-600 dark:text-red-400">{statsError[room.id]}</p>
                      ) : (roomStats[room.id]?.length ?? 0) === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No student performance data yet.</p>
                      ) : (
                        <div className="grid gap-2">
                          {roomStats[room.id]?.map((stat) => (
                            <div
                              key={stat.student_id}
                              className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
                            >
                              <div className="flex-1 min-w-0">
                                {stat.university && (
                                  <p className="text-xs font-medium text-green-700 dark:text-green-300 truncate">
                                    {stat.university}
                                  </p>
                                )}
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {stat.name || stat.username || 'Student'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {stat.email || 'No email'}
                                </p>
                              </div>
                              <div className="flex items-center space-x-4">
                                <div className="text-right">
                                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Questions</p>
                                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {stat.questions_answered}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Avg Score</p>
                                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {stat.average_score !== undefined ? `${Math.round(stat.average_score)}%` : '—'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  </React.Fragment>
                ))}
              </div>
            );
            })}
          </div>
        )}
      </div>

      {/* Create Room Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Create New Learning Room
              </h2>
            </div>
            
            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setCreateRoomTab('single')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  createRoomTab === 'single'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Group Room
              </button>
              <button
                type="button"
                onClick={() => setCreateRoomTab('all')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  createRoomTab === 'all'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Individual Rooms
              </button>
            </div>
            
            <form onSubmit={handleCreateRoom} className="p-6 space-y-4">
              {createRoomTab === 'single' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Room Name
                  </label>
                  <input
                    type="text"
                    required
                    value={createForm.name}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter room name"
                  />
                </div>
              )}
              
              {createRoomTab === 'all' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Personal rooms will be created for all active students. Room names will be automatically generated as "[Subject Name] - [Student Name]".
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject
                </label>
                <select
                  required
                  value={createForm.subjectId}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, subjectId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a subject</option>
                  {subjects.map(subject => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Min Success Score (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={createForm.minSuccessScore}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, minSuccessScore: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Modifier (optional)
                </label>
                <textarea
                  value={createForm.modifier}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, modifier: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Guide the AI tone/format, e.g. 'be brief and Socratic'"
                />
              </div>

              <AiClassmateFields isGroupRoom={createRoomTab === 'single'} name={createForm.aiStudentName} description={createForm.aiStudentDescription}
                onNameChange={value => setCreateForm(prev => ({ ...prev, aiStudentName: value }))}
                onDescriptionChange={value => setCreateForm(prev => ({ ...prev, aiStudentDescription: value }))} />

              <div className={`grid gap-4 ${createRoomTab === 'single' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Hint Threshold
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={createForm.hintThreshold}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, hintThreshold: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                {createRoomTab === 'single' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Consecutive Answers
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={createForm.allowedConsequentAnswers}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, allowedConsequentAnswers: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateRoomTab('single'); // Reset to default tab
                    setCreateForm({
                      name: '',
                      subjectId: '',
                      minSuccessScore: 70,
                      hintThreshold: 2,
                      allowedConsequentAnswers: 1,
                      modifier: '',
                      aiStudentName: DEFAULT_AI_STUDENT_NAME,
                      aiStudentDescription: DEFAULT_AI_STUDENT_DESCRIPTION
                    });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Create Room
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && roomToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Delete Room
              </h2>
            </div>
            
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to permanently delete the room "{roomToDelete.name}"? 
                This action cannot be undone and will remove all room data including messages and scores.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setRoomToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteRoom}
                  className="flex-1 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { RoomBrowserPage };
